import { formatDateKey, parseDateKey } from './helpers';

/**
 * Converts date from "DD Mon YYYY" to "YYYY-MM-DD"
 * Example: "31 Jan 2026" -> "2026-01-31"
 */
export function formatToIsoDate(dateStr: string): string {
  if (!dateStr) return "";
  const [day, rawMonth, year] = dateStr.trim().split(" ");
  const monthStr = rawMonth.replace(",", ""); // safety

  const monthMap: Record<string, string> = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  const month = monthMap[monthStr];

  if (!month) {
    throw new Error(`Invalid month format: ${monthStr}`);
  }
  const formatedDate = `${year}-${month}-${day.padStart(2, "0")}`

  return formatedDate
}



export const formatProjectTimeline = (start: string, end?: string): string => {
  const startDate = new Date(start);

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };

  const startFormatted = startDate.toLocaleDateString('en-US', options);

  // If no end date provided, return just the start date
  if (!end) {
    return startFormatted;
  }

  const endDate = new Date(end);
  const endFormatted = endDate.toLocaleDateString('en-US', options);

  return `${startFormatted} - ${endFormatted}`;
};


export const getDaysLeft = (end: string): number => {
  const today = new Date();
  const endDate = new Date(end);

  const diffTime = endDate.getTime() - today.getTime();
  return Math.max(Math.ceil(diffTime / (1000 * 60 * 60 * 24)), 0);
};


export const formatIsoToReadableDate = (iso: string): string => {
  const date = new Date(iso);

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};


export const formatDate = (date: Date | string) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
};

export const parseDate = (dateStr?: string) => {
  if (!dateStr) return new Date(0);

  // Check if it's already a valid date string that new Date() can handle
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) return date;

  // If it's missing the Z or offset but looks like ISO, try appending Z
  if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
    const gmtDate = new Date(`${dateStr}Z`);
    if (!isNaN(gmtDate.getTime())) return gmtDate;
  }

  return new Date(0);
};

export const timeAgo = (iso: string | null): string => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const formatDateToYmd = (date: Date | string | null): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatIsoToTime = (iso?: string | null, timeZone?: string | null): string => {
  if (!iso) return '-';
  const trimmed = iso.trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    const [h, m] = trimmed.split(':').map(Number);
    const date = new Date();
    date.setHours(h, m, 0);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(timeZone ? { timeZone } : {}),
    });
  }
  const d = new Date(trimmed);
  if (isNaN(d.getTime())) {
    return iso;
  }
  try {
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(timeZone ? { timeZone } : {}),
    });
  } catch {
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
};

export const extractTimeForInput = (iso?: string | null, timeZone?: string | null): string => {
  if (!iso) return '';
  const trimmed = iso.trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    const [h, m] = trimmed.split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  }
  const d = new Date(trimmed);
  if (isNaN(d.getTime())) return '';
  try {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      ...(timeZone ? { timeZone } : {}),
    };
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(d);
    let hours = '00';
    let minutes = '00';
    parts.forEach(part => {
      if (part.type === 'hour') hours = part.value;
      if (part.type === 'minute') minutes = part.value;
    });
    if (hours === '24') hours = '00';
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  } catch {
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }
};

/**
 * Normalizes a backend "attendanceDate" value into a "YYYY-MM-DD" calendar-day
 * key. Plain date strings ("2026-08-19") are returned as-is. Full timestamps
 * ("2026-08-19T01:00:00.000Z") are converted into the given IANA timezone
 * before extracting the day, instead of naively slicing off the UTC date —
 * a blind slice silently shifts the day by ±1 for any timezone that isn't
 * UTC (e.g. a late-evening check-in in a timezone behind UTC rolls into the
 * next UTC calendar day), which is how "today's" attendance can end up
 * mapped to yesterday's or tomorrow's calendar cell.
 */
export const toAttendanceDateKey = (dateStr: string, timeZone?: string | null): string => {
  if (!dateStr) return '';
  if (!dateStr.includes('T')) return dateStr;

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr.split('T')[0];

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).formatToParts(d);
    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    // Unknown/invalid timeZone — fall back to the naive UTC slice below.
  }
  return dateStr.split('T')[0];
};

/**
 * Today's date key (YYYY-MM-DD) as a given IANA timezone's clock reads it —
 * not the viewer's device. An admin in New York at 11 PM is already a day
 * ahead in Asia/Kolkata. Falls back to the device timezone when none is given.
 *
 * Read fresh on every call rather than cached, so anything that calls it per
 * render moves "today" forward when that timezone's midnight passes.
 */
export const todayInTimezone = (timeZone?: string | null): string =>
  toAttendanceDateKey(new Date().toISOString(), timeZone);

/**
 * True when `dateKey` is strictly after today in *every* given timezone — today
 * itself counts as restricted. One date applied to several policies has to be
 * in the future for the furthest-ahead of them, so a single timezone where the
 * date is today or past fails it. An empty list means the device timezone.
 */
export const isFutureDateKeyIn = (
  dateKey: string,
  timeZones: (string | null | undefined)[],
): boolean => {
  const zones = timeZones.length > 0 ? timeZones : [undefined];
  return zones.every((zone) => dateKey > todayInTimezone(zone));
};

/** The date key (YYYY-MM-DD) for the day before `dateKey`. */
export const dayBefore = (dateKey: string): string => {
  const d = parseDateKey(dateKey);
  d.setDate(d.getDate() - 1);
  return formatDateKey(d);
};

/**
 * Holiday Calendar coverage `effectiveTo` is exclusive — "2027-07-01" means
 * coverage ends on 30 June. This is the last day actually covered, which is
 * what an "Effective To" column should show.
 */
export const lastCoveredDateKey = (effectiveTo: string): string => dayBefore(effectiveTo);

/**
 * Returns the current UTC offset for a given IANA timezone (or browser default)
 * in canonical ISO-8601 format: ±HH:MM (e.g. "+05:30", "-08:00", "+00:00").
 */
export const getCanonicalUtcOffset = (tz?: string | null): string => {
  const targetTz = tz || 'Asia/Kolkata';
  try {
    const date = new Date();
    const str = date.toLocaleString('en-US', { timeZone: targetTz, timeZoneName: 'shortOffset' });
    const match = str.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (match) {
      const sign = match[1];
      const hours = match[2].padStart(2, '0');
      const minutes = (match[3] || '00').padStart(2, '0');
      return `${sign}${hours}:${minutes}`;
    }
  } catch (e) {
    // Fallback if timezone string is invalid
  }

  // System local timezone offset fallback
  try {
    const date = new Date();
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
    const minutes = String(absMinutes % 60).padStart(2, '0');
    return `${sign}${hours}:${minutes}`;
  } catch (e) {
    return '+05:30';
  }
};

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * "2026-08-03" | "2026-08-03T..." -> "3 Aug 2026"
 * Date-only strings are read as calendar days (no UTC shift).
 */
export const formatDayMonthYear = (
  value?: string | Date | null,
  fallback = '—',
): string => {
  if (!value) return fallback;

  // Date-only: parse the parts so we don't get UTC-midnight day slippage.
  if (typeof value === 'string') {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const [, y, mo, d] = m;
      const monthIdx = Number(mo) - 1;
      if (monthIdx < 0 || monthIdx > 11) return fallback;
      return `${Number(d)} ${SHORT_MONTHS[monthIdx]} ${y}`;
    }
  }

  const d = new Date(value);
  if (isNaN(d.getTime())) return fallback;
  return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};


/**
 * Timestamps from today render as a time ("3:42 PM"); anything older renders
 * as "3 Aug". Used for created/updated stamps where recency matters more
 * than the calendar day.
 */
export const formatTimeIfToday = (
  value?: string | Date | null,
  fallback = '—',
): string => {
  if (!value) return fallback;

  const d = new Date(value);
  if (isNaN(d.getTime())) return fallback;

  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  if (isToday) {
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;
};